/**
 * IT PDP — the "Discover more" / "Pair with…" panel.
 *
 * Both panels are server-rendered; this decides which one shows and drives the
 * arrows that step the visible one. That matters here: the block lives inside
 * the main PDP section, so anything that re-fetched and morphed would tear
 * down the gallery, the variant store and the buy form along with it.
 *
 * The rail itself is a CSS scroll-snap slider, so swipe, trackpad and keyboard
 * already work with no script. The arrows only call `scrollBy` on it by one
 * tile, and step aside (hidden) when the row does not overflow.
 */
class ItPdpDiscover extends HTMLElement {
  #raf = 0;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    for (const panel of this.querySelectorAll('[data-panel]')) {
      panel.addEventListener('scroll', this.#onScroll, { passive: true });
    }
    this.#resize = new ResizeObserver(() => this.#sync());
    this.#resize.observe(this);
    this.#sync();
  }

  disconnectedCallback() {
    this.#resize?.disconnect();
  }

  #resize = null;

  get #panel() {
    return this.querySelector('[data-panel]:not([hidden])') ?? this.querySelector('[data-panel]');
  }

  #onClick = (event) => {
    const arrow = event.target.closest('.it-discover__arrow');
    if (arrow && this.contains(arrow)) {
      this.#step(Number(arrow.dataset.step) || 1);
      return;
    }

    const tab = event.target.closest('.it-discover__tab');
    if (!tab || !this.contains(tab)) return;

    const name = tab.dataset.tab;
    if (!name) return;

    for (const other of this.querySelectorAll('.it-discover__tab')) {
      const on = other === tab;
      other.classList.toggle('is-on', on);
      other.setAttribute('aria-selected', String(on));
    }

    for (const panel of this.querySelectorAll('[data-panel]')) {
      panel.hidden = panel.dataset.panel !== name;
    }

    this.#sync();
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => this.#sync());
  };

  /** One tile plus its gap — measured, so the CSS stays the only source of size. */
  #stride(panel) {
    const [a, b] = panel.children;
    if (a && b) return b.offsetLeft - a.offsetLeft;
    return a?.offsetWidth ?? panel.clientWidth;
  }

  #step(direction) {
    const panel = this.#panel;
    if (!panel) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    panel.scrollBy({ left: this.#stride(panel) * direction, behavior: reduce ? 'auto' : 'smooth' });
  }

  #sync() {
    const panel = this.#panel;
    const arrows = this.querySelector('.it-discover__arrows');
    if (!panel || !arrows) return;

    const max = panel.scrollWidth - panel.clientWidth;
    arrows.hidden = max <= 1;

    const at = panel.scrollLeft;
    for (const arrow of arrows.querySelectorAll('.it-discover__arrow')) {
      const dir = Number(arrow.dataset.step) || 1;
      arrow.disabled = dir < 0 ? at <= 1 : at >= max - 1;
    }
  }
}

if (!customElements.get('it-pdp-discover')) {
  customElements.define('it-pdp-discover', ItPdpDiscover);
}
